import * as React from 'react';
import { useLayoutEffect } from 'react';
import {
  SxProps,
  TextField as MatTextField,
  TextFieldProps as MatTextFieldProps,
  InputAdornment,
  InputAdornmentProps,
  Theme,
  formHelperTextClasses,
  filledInputClasses,
  iconButtonClasses
} from '@mui/material';
import { customArcherColors } from '../../utils';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import { useSafeId } from '../../hooks';

export type TextFieldProps = {
  value?: string | unknown;
  id?: string;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: string | React.ReactNode;
  label?: string | React.ReactNode;
  placeholder?: string;
  type?: string;
  variant?: 'filled' | 'outlined' | 'standard';
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
  onClick?: (event: any) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  required?: boolean;
  margin?: 'dense' | 'normal' | 'none';
  startAdornment?: string | React.ReactNode;
  endAdornment?: string | React.ReactNode;
  readonly?: boolean;
  InputProps?: MatTextFieldProps['InputProps'];
  translate?: any;
  autoComplete?: string;
  autoFocus?: boolean;
  ref?: MatTextFieldProps['ref'];
  className?: string;
  dataProp?: string;
  defaultValue?: string | unknown;
  ariaLabelledBy?: string;
  sx?: SxProps<Theme>;
};

const useStyles = () => {
  return {
    root: {
      flex: '0 1 auto',
      '& >div': {
        maxHeight: '3rem'
      },
      [`& .${formHelperTextClasses['contained']}`]: {
        lineHeight: '.75rem',
        bottom: 'unset',
        top: '3rem'
      },
      [`& .${filledInputClasses['adornedEnd']}`]: {
        paddingRight: '9.5px'
      },
      [`& .${iconButtonClasses['root']}`]: {
        padding: 0
      }
    },
    adornmentIconStyle: {
      [`& .${iconButtonClasses['root']}`]: {
        margin: 0
      }
    },
    adornmentIconErrorStyle: {
      '& svg': {
        color: customArcherColors.alertColors.error,
        alignSelf: 'center'
      }
    },
    endAdornmentTypography: { alignSelf: 'flex-end', position: 'relative' }
  };
};

type CustomAdornmentType = { iconStyle: SxProps<Theme>; iconErrorStyle: SxProps<Theme>; typographyStyle: SxProps<Theme> };

const generateAdornment = (
  classNames: CustomAdornmentType,
  startAdornment?: string | React.ReactNode,
  endAdornment?: string | React.ReactNode,
  error = false
) => {
  const { iconStyle, iconErrorStyle, typographyStyle } = classNames;

  const assembleAdornment = (adornment: any, position: string) => {
    let assembledAdornment;
    if (position === 'start') {
      assembledAdornment = adornment;
    }

    if (position === 'end') {
      assembledAdornment = (
        <React.Fragment>
          {adornment && typeof adornment === 'string' ? <Typography sx={typographyStyle}>{adornment}</Typography> : null}
          {adornment && typeof adornment === 'object' && adornment.icon && <Icon icon={adornment.icon} fontSize="large" color="primary" />}
          {error ? <Icon icon="Error" color="inherit" fontSize="large" /> : null}
        </React.Fragment>
      );
    }
    return (
      <InputAdornment sx={error ? iconErrorStyle : iconStyle} position={position as InputAdornmentProps['position']}>
        {assembledAdornment}
      </InputAdornment>
    );
  };

  return {
    startAdornment: startAdornment ? assembleAdornment(startAdornment, 'start') : undefined,
    endAdornment: endAdornment || error ? assembleAdornment(endAdornment, 'end') : undefined
  };
};

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      startAdornment,
      endAdornment,
      label = '',
      placeholder,
      required,
      error = false,
      helperText,
      id,
      variant = 'filled',
      readonly,
      InputProps = {},
      disabled,
      onClick,
      autoComplete = 'off',
      autoFocus,
      className,
      dataProp,
      ariaLabelledBy,
      sx,
      ...rest
    },
    ref
  ) => {
    const { root, adornmentIconStyle, adornmentIconErrorStyle, endAdornmentTypography } = useStyles();
    const adornmentStyles: CustomAdornmentType = {
      iconStyle: adornmentIconStyle,
      iconErrorStyle: adornmentIconErrorStyle,
      typographyStyle: endAdornmentTypography
    };
    let adornmentProps;
    if (error || startAdornment || endAdornment) {
      adornmentProps = generateAdornment(adornmentStyles, startAdornment, endAdornment, error);
    }

    const safeId = useSafeId(id);
    useLayoutEffect(() => {
      if (helperText) {
        document.getElementById(`${safeId}-helper-text`)?.setAttribute('aria-label', helperText as string);
      }
    }, [helperText, safeId]);

    return (
      <MatTextField
        ref={ref}
        {...rest}
        id={safeId}
        error={error}
        required={required}
        disabled={disabled}
        helperText={helperText}
        sx={[root, sx] as SxProps<Theme>}
        label={label}
        placeholder={placeholder}
        variant={variant}
        onClick={onClick}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        className={className}
        InputProps={{
          ...adornmentProps,
          readOnly: readonly,
          inputProps: {
            'aria-label': `${label || placeholder}`,
            'aria-readonly': readonly,
            'data-selector': dataProp ? dataProp : null,
            'aria-labelledby': ariaLabelledBy ? ariaLabelledBy : undefined
          },
          ...InputProps
        }}
      />
    );
  }
);

TextField.displayName = 'TextField';

export { TextField };

TextField.defaultProps = {
  fullWidth: true,
  readonly: false,
  autoFocus: false
};
